
import React, { useState } from 'react';
import { Heart, Sun, Trash2, Pencil, Share2, CheckCircle2, FileEdit } from 'lucide-react';
import { ReportTemplate, Category } from '../types';
import { copyHtmlToClipboard, copyToClipboard } from '../services/clipboardUtils';
import { sanitizeHtml } from '../utils/sanitize';

interface ReportCardProps {
  report: ReportTemplate;
  categories: Category[];
  onEdit: (report: ReportTemplate) => void;
  onDelete: (id: string) => void;
  onFavoriteToggle: (id: string) => void;
  onShare: (report: ReportTemplate) => void;
  onIA: (report: ReportTemplate) => void;
  onUse?: (id: string) => void; 
  isDarkMode?: boolean;
}

const ReportCard: React.FC<ReportCardProps> = ({ report, categories, onEdit, onDelete, onFavoriteToggle, onShare, onIA, onUse, isDarkMode }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyAction = async () => {
    // FIX: Se usa la misma lógica que en los ajustes para evitar inversión.
    const savedVal = localStorage.getItem('modular_copy_rich_text');
    const useRichText = savedVal === null ? true : savedVal === 'true';
    let success = false;
    
    if (useRichText) {
      success = await copyHtmlToClipboard(report.content);
    } else {
      // Limpiamos etiquetas HTML para texto plano preservando estructura
      const plainText = report.content
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n\n')
        .replace(/<\/div>/gi, '\n')
        .replace(/<[^>]*>?/gm, '')
        .replace(/&nbsp;/g, ' ')
        .trim();
      success = await copyToClipboard(plainText);
    }

    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      if (onUse) onUse(report.id);
    }
  };

  const getFilterInfo = (filterId: string) => {
    for (const cat of categories) {
      const filter = cat.filters.find(f => f.id === filterId);
      if (filter) {
        const colorBase = cat.color?.split('-')[1] || 'blue';
        const styleClass = isDarkMode 
          ? `bg-${colorBase}-900/10 text-${colorBase}-400 border border-${colorBase}-500/20`
          : `bg-${colorBase}-50 text-${colorBase}-600 border border-${colorBase}-100`;
        return { name: filter.name, style: styleClass };
      }
    }
    return null;
  };

  return (
    <div className={`rounded-[28px] shadow-sm p-6 mb-4 relative border transition-all hover:shadow-md ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-slate-100'}`}>
      {/* Indicador de copiado */}
      {copied && (
        <div className="absolute top-4 right-6 flex items-center space-x-1.5 bg-green-500 text-white px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest shadow-lg animate-in fade-in zoom-in duration-200 z-10">
          <CheckCircle2 size={10} />
          <span>Copiado</span>
        </div>
      )}

      {/* Área cliqueable para copiar (Título + Contenido) */}
      <div onClick={handleCopyAction} className="cursor-pointer group relative">
        <div className="flex justify-between items-start mb-2 pr-16">
          <h3 className={`font-['Outfit'] font-black text-[15px] leading-tight w-full uppercase tracking-tight transition-colors ${isDarkMode ? 'text-gray-100' : 'text-[#1e293b] group-hover:text-blue-600'}`}>
            {report.title}
          </h3>
        </div>
        <div 
          className={`text-[13px] leading-relaxed line-clamp-3 font-medium transition-colors mb-4 report-html-content ${isDarkMode ? 'text-gray-400 group-hover:text-gray-200' : 'text-slate-500 group-hover:text-slate-800'}`}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(report.content) }}
        />
      </div>

      {/* Footer con iconos de acción */}
      <div className="flex items-end justify-between mt-2">
        <div className="flex flex-wrap gap-2 mb-1">
          {report.filterIds.slice(0, 3).map(fid => {
            const info = getFilterInfo(fid);
            if (!info) return null;
            return (
              <span key={fid} className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-[0.15em] ${info.style}`}>
                {info.name}
              </span>
            );
          })}
        </div>
        
        <div className="flex items-center space-x-1">
            <button onClick={() => onFavoriteToggle(report.id)} className={`p-2 rounded-full transition-colors ${report.isFavorite ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-400 hover:bg-slate-50'}`} title="Favorito"><Heart size={18} fill={report.isFavorite ? "currentColor" : "none"}/></button>
            <button onClick={() => onShare(report)} className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50/10 rounded-full transition-all" title="Compartir"><Share2 size={18} /></button>
            <div className="h-4 w-px bg-gray-200 dark:bg-gray-700 mx-1.5"></div>
            
            <button onClick={() => { onIA(report); onUse?.(report.id); }} className="p-2 text-gray-400 hover:text-indigo-500 hover:bg-indigo-50/10 rounded-full transition-all" title="MODULAR-1"><Sun size={18} /></button>
            
            <div className="h-4 w-px bg-gray-200 dark:bg-gray-700 mx-1.5"></div>
            
            <button onClick={() => onEdit(report)} className="p-2 text-gray-400 hover:text-slate-600 hover:bg-slate-50/10 rounded-full transition-all" title="Editar"><Pencil size={18} /></button>
            <button onClick={() => onDelete(report.id)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50/10 rounded-full transition-all" title="Eliminar"><Trash2 size={18} /></button>
        </div>
      </div>
      <style>{`
        .report-html-content b { font-weight: 800; }
      `}</style>
    </div>
  );
};

export default ReportCard;
